import { appSettings } from '@/appSettings';

const DISTINCT_ID_KEY = 'umami.distinctId';

let _umamiInitialized = false;

type UseUmamiReturn = {
    init: () => void;
    identify: () => void;
    reset: () => void;
    track: (name: string, data?: Record<string, unknown>) => void;
};

function readDistinctId(): string | null {
    try {
        return localStorage.getItem(DISTINCT_ID_KEY);
    } catch {
        return null;
    }
}

function writeDistinctId(id: string | null): void {
    try {
        if (id == null) localStorage.removeItem(DISTINCT_ID_KEY);
        else localStorage.setItem(DISTINCT_ID_KEY, id);
    } catch {
        // Storage blocked: the id then only lasts until the next reload.
    }
}

/**
 * Developers often share one login, so visitors are told apart by a random id
 * per browser login instead of by user. It survives reloads and is dropped on logout.
 */
export function useUmami(): UseUmamiReturn {
    function init(): void {
        if (_umamiInitialized) return;
        if (!appSettings.enableUmami) return;
        if (appSettings.umamiScriptUrl === '' || appSettings.umamiWebsiteId === '') return;

        // The tracker records page views itself, including history-based route changes.
        const scriptElement = document.createElement('script');
        scriptElement.defer = true;
        scriptElement.src = appSettings.umamiScriptUrl;
        scriptElement.dataset.websiteId = appSettings.umamiWebsiteId;
        scriptElement.dataset.performance = 'true';

        const distinctId = readDistinctId();
        if (distinctId != null) scriptElement.dataset.distinctId = distinctId;

        scriptElement.addEventListener('load', () => {
            const id = readDistinctId();
            if (id != null && id !== distinctId) window.umami?.identify(id);
        });

        document.head.appendChild(scriptElement);
        _umamiInitialized = true;
    }

    function identify(): void {
        if (!appSettings.enableUmami) return;
        if (readDistinctId() != null) return;

        const id = crypto.randomUUID();
        writeDistinctId(id);
        window.umami?.identify(id);
    }

    function reset(): void {
        writeDistinctId(null);
    }

    function track(name: string, data?: Record<string, unknown>): void {
        if (!_umamiInitialized) return;
        window.umami?.track(name, data);
    }

    return { init, identify, reset, track };
}
